/**
 * RecommendationCard Component
 *
 * Displays the primary recommendation (REPAIR, REPLACE, CONSIDER, MAINTAIN)
 * with visual styling based on the recommendation type
 */

export default function RecommendationCard({ results }) {
  const { recommendation, assessmentScore, repairVsThreshold, threshold } = results;

  // Recommendation-specific configurations
  const recommendationConfig = {
    REPLACE: {
      icon: '🔄',
      title: 'Replace System',
      bgColor: '#fef3c7',
      borderColor: '#f59e0b',
      textColor: '#92400e',
      message: 'Based on this assessment, replacing your HVAC system is the recommended course of action.'
    },
    REPAIR: {
      icon: '🔧',
      title: 'Repair System',
      bgColor: '#d1fae5',
      borderColor: '#10b981',
      textColor: '#065f46',
      message: 'Based on this assessment, repairing your current HVAC system makes the most economic sense.'
    },
    CONSIDER: {
      icon: '🤔',
      title: 'Consider Replacement',
      bgColor: '#dbeafe',
      borderColor: '#3b82f6',
      textColor: '#1e3a8a',
      message: 'Your system is at a decision point. Review the analysis below to determine the best option for your situation.'
    },
    MAINTAIN: {
      icon: '✅',
      title: 'Maintain & Monitor',
      bgColor: '#dcfce7',
      borderColor: '#22c55e',
      textColor: '#14532d',
      message: 'Your system is in good condition. Continue with regular maintenance and monitoring.'
    }
  };

  const config = recommendationConfig[recommendation] || recommendationConfig.MAINTAIN;

  return (
    <div style={{
      background: config.bgColor,
      border: `3px solid ${config.borderColor}`,
      borderRadius: '12px',
      padding: '30px',
      marginBottom: '30px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      {/* Main Recommendation */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '10px'
        }}>
          {config.icon}
        </div>
        <h2 style={{
          margin: '0 0 10px 0',
          color: config.textColor,
          fontSize: '32px',
          fontWeight: '700'
        }}>
          {config.title}
        </h2>
        <p style={{
          margin: '0',
          color: config.textColor,
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          {config.message}
        </p>
      </div>

      {/* Assessment Score */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '40px',
        flexWrap: 'wrap',
        paddingTop: '20px',
        borderTop: `2px solid ${config.borderColor}`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '14px',
            color: config.textColor,
            fontWeight: '600',
            marginBottom: '5px',
            opacity: 0.8
          }}>
            Assessment Score
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: config.textColor
          }}>
            {assessmentScore}<span style={{ fontSize: '20px' }}>/100</span>
          </div>
        </div>

        {repairVsThreshold > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '14px',
              color: config.textColor,
              fontWeight: '600',
              marginBottom: '5px',
              opacity: 0.8
            }}>
              Repair Cost vs Threshold
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              color: config.textColor
            }}>
              {Math.round(repairVsThreshold)}<span style={{ fontSize: '20px' }}>%</span>
            </div>
            <div style={{
              fontSize: '12px',
              color: config.textColor,
              opacity: 0.7,
              marginTop: '5px'
            }}>
              (Threshold: ${threshold.toLocaleString()})
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
